// src/components/layout/AgentWorkspaceLayout.jsx
import React, { useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box, Typography, Tabs, Tab, CircularProgress, Paper, Breadcrumbs, Link as MuiLink,
  IconButton, Tooltip, Stack, Button, Alert, useTheme // Added useTheme
} from '@mui/material';
import {
  Home as OverviewIcon,
  ChatBubbleOutline as InteractIcon,
  Settings as SettingsIcon,
  Description as PromptIcon,
  Build as BuildIcon,
  Widgets as WidgetsIcon,
  Forum as ForumIcon,
  ArrowBack as BackToGlobalIcon,
  Edit as EditAgentIcon,
  ContentCopy as CopyIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { useAgentContext } from '../../context/AgentContext';
import { toast } from 'react-toastify';

const AGENT_WORKSPACE_TABS = [
  { label: 'Overview', path: 'overview', icon: <OverviewIcon /> },
  { label: 'Interact', path: 'interact', icon: <InteractIcon /> },
  { label: 'Settings', path: 'settings', icon: <SettingsIcon /> },
  { label: 'Prompts', path: 'prompt', icon: <PromptIcon /> },
  { label: 'Tools', path: 'tools', icon: <BuildIcon /> },
  { label: 'Widgets', path: 'widgets', icon: <WidgetsIcon /> },
  { label: 'Conversations', path: 'conversations', icon: <ForumIcon /> },
];

const AgentWorkspaceLayout = () => {
  const { agentId } = useParams();
  const { currentAgent, loadingAgentContext, deselectAgent, isNavigatingToAgent } = useAgentContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme(); // Get theme for consistent spacing if needed

  const pathSegments = location.pathname.split('/');
  const currentTabPath = pathSegments[pathSegments.length -1];
  const activeTab = AGENT_WORKSPACE_TABS.find(tab => tab.path === currentTabPath)?.path || AGENT_WORKSPACE_TABS[0].path;
  
  const activeTabConfig = AGENT_WORKSPACE_TABS.find(tab => tab.path === activeTab);
  const activeTabLabel = activeTabConfig ? activeTabConfig.label : "Detail";

  useEffect(() => {
    if (agentId && !loadingAgentContext && !isNavigatingToAgent && !currentAgent) {
        console.warn(`AgentWorkspaceLayout: Agent ${agentId} not found in context after load attempt.`);
    }
  }, [agentId, currentAgent, loadingAgentContext, isNavigatingToAgent, navigate]);

  const handleTabChange = (event, newValue) => {
    navigate(`/agent-workspace/${agentId}/${newValue}`);
  };

  const handleCopyAgentId = () => {
    if (!agentId) return;
    navigator.clipboard.writeText(agentId)
      .then(() => toast.success('Agent ID copied to clipboard!'))
      .catch(() => toast.error('Failed to copy Agent ID.'));
  };

  if (loadingAgentContext || isNavigatingToAgent) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, flexGrow: 1 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Agent Workspace {agentId ? `for ...${agentId.slice(-6)}` : ''}...</Typography>
      </Box>
    );
  }
  
  if (!currentAgent && agentId) { 
      return (
          <Box sx={{p:0, m:0, flexGrow:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center'}}>
              <Alert severity="error" sx={{mb:2, width: '100%', maxWidth: '600px'}}>
                  Could not load data for Agent ID: {agentId}. The agent may not exist or an error occurred.
              </Alert>
              <Button onClick={() => navigate('/agents')} startIcon={<BackToGlobalIcon />} variant="outlined">
                  View All Agents
              </Button>
          </Box>
      );
  }

  if (!currentAgent) {
       return (
          <Box sx={{p:0, m:0, flexGrow:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center'}}>
              <Alert severity="info" sx={{mb:2, width: '100%', maxWidth: '600px'}}>
                No agent is currently selected. Please select an agent from the list or dashboard.
              </Alert>
              <Button onClick={() => navigate('/dashboard')} startIcon={<BackToGlobalIcon />} variant="outlined">
                  Go to Dashboard
              </Button>
          </Box>
       );
  }

  return (
    // This root Box should NOT have outer padding. AdminLayout's content area provides it.
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 /* p:0 is default */ }}>
      
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb" 
        sx={{ mb: theme.spacing(2) }} // Use theme spacing for consistency
      >
        <MuiLink component="button" onClick={deselectAgent} underline="hover" color="inherit" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
           Global Dashboard
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/agents">
          All Agents
        </MuiLink>
        <Typography color="text.primary" sx={{fontWeight: 500}}>{currentAgent.name}</Typography>
        {activeTabLabel && <Typography color="text.secondary">{activeTabLabel}</Typography>}
      </Breadcrumbs>

      <Paper 
        elevation={0} // Or 1 for slight lift, depending on theme preference
        sx={{ 
            p: theme.spacing(2), // Consistent padding inside the paper
            mb: theme.spacing(3), // Margin below this header section
            border: `1px solid ${theme.palette.divider}`, 
            borderRadius: 2  // Match other rounded corners
        }}
      >
        <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems="center" spacing={{xs: 2, sm: 1}}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              {currentAgent.name}
              <Tooltip title="Copy Agent ID">
                <IconButton onClick={handleCopyAgentId} size="small" sx={{ ml: 1 }}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              Agent Workspace (ID: {agentId ? agentId.substring(0,8) : 'N/A'}...)
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{alignSelf: {xs: 'flex-start', sm: 'center'}}}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditAgentIcon />}
              onClick={() => navigate(`/agent-workspace/${agentId}/settings`)}
            >
              Agent Settings
            </Button>
            <Button
              variant="text"
              color="inherit"
              size="small"
              startIcon={<BackToGlobalIcon />}
              onClick={deselectAgent}
            >
              Exit Workspace
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: theme.spacing(3) }}> {/* Margin below tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Agent workspace tabs"
        >
          {AGENT_WORKSPACE_TABS.map((tabInfo) => (
            <Tab
              key={tabInfo.path}
              icon={tabInfo.icon}
              iconPosition="start"
              label={tabInfo.label}
              value={tabInfo.path}
              sx={{ textTransform: 'none', minHeight: 48, fontWeight: activeTab === tabInfo.path ? 600 : 400 }}
            />
          ))}
        </Tabs>
      </Box>

      {/* This Box will contain the actual tab content. */}
      {/* The tab content itself (e.g., AgentOverviewTab) should handle its own internal padding if elements like Paper are used. */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <Outlet context={{ agent: currentAgent }} />
      </Box>
    </Box>
  );
};

export default AgentWorkspaceLayout;